// @ts-check

import { Component } from '@theme/component';

/**
 * @typedef {object} SyncedMediaRefs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLVideoElement[]} [videos]
 */

/**
 * Synced Media Component
 *
 * Synchronizes media slides with accordion open/close events.
 * When an accordion opens, the corresponding slide becomes visible.
 *
 * @extends Component<SyncedMediaRefs>
 */
class SyncedMediaComponent extends Component {
  /** @type {string | undefined} */
  sectionId;

  /** @type {number} */
  currentIndex = 0;

  /** @type {NodeListOf<HTMLDetailsElement> | HTMLDetailsElement[]} */
  accordions = [];

  connectedCallback() {
    super.connectedCallback();
    this.sectionId = this.dataset.sectionId;
    this.#findAccordions();
    this.#bindEvents();
    this.#playActiveVideo();
  }

  #findAccordions() {
    const section = this.closest('section, .shopify-section');
    if (section) {
      this.accordions = section.querySelectorAll('.accordion');
    }
  }

  #bindEvents() {
    this.accordions.forEach((accordion, index) => {
      accordion.addEventListener('toggle', () => {
        if (accordion.open) {
          this.#showSlide(index);
        }
      });
    });
  }

  /**
   * @param {number} index
   */
  #showSlide(index) {
    const slides = this.refs.slides ?? [];

    if (index === this.currentIndex || index >= slides.length) return;

    // Pause video in current slide if any
    this.#pauseVideoInSlide(slides[this.currentIndex]);

    // Hide current slide
    slides[this.currentIndex]?.classList.remove('iwt-media-sync__slide--active');

    // Show new slide
    slides[index]?.classList.add('iwt-media-sync__slide--active');

    // Play video in new slide if any
    this.#playVideoInSlide(slides[index]);

    this.currentIndex = index;
  }

  /**
   * Play video in the first active slide on load
   */
  #playActiveVideo() {
    const slides = this.refs.slides ?? [];
    const activeSlide = slides[this.currentIndex];
    if (activeSlide) {
      this.#playVideoInSlide(activeSlide);
    }
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  #playVideoInSlide(slide) {
    if (!slide || slide.dataset.mediaType !== 'video') return;
    const video = slide.querySelector('video');
    if (video) {
      // Lazy load: set preload to auto when playing
      video.preload = 'auto';
      video.play().catch(() => {
        // Autoplay might be blocked, ignore error
      });
    }
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  #pauseVideoInSlide(slide) {
    if (!slide || slide.dataset.mediaType !== 'video') return;
    const video = slide.querySelector('video');
    if (video) {
      video.pause();
    }
  }
}

if (!customElements.get('synced-media-component')) {
  customElements.define('synced-media-component', SyncedMediaComponent);
}
